import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useToast } from "@flat-ui";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { isMemberActive } from "@utils/user-utils";
import {
  ProjectMemberEvents,
  TableType,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { BaseCompanyIdProps } from "@custom-types/base-types";

interface UseResendInvitationParams extends BaseCompanyIdProps {
  /** Context in which the members table is used */
  subjectType: MemberTableSubject;

  /** The selected project (for project member tables) */
  selectedProject?: SdbProject | null;

  /** Whether the user has permission to resend invitations at company level */
  canResendInvitationToCompany: boolean;
}

interface UseResendInvitationReturn {
  /** Resend invitation for a pending member */
  resendInvitation: (member: SdbMemberTypes) => Promise<void>;

  /** Whether the resend invitation action should be shown for the given member */
  shouldShowResendInvitation: (member: SdbMemberTypes) => boolean;
}

/**
 * Hook for resending membership invitations.
 * Handles both project and workspace/group/team invitation resending.
 */
export function useResendInvitation({
  companyId,
  subjectType,
  selectedProject,
  canResendInvitationToCompany,
}: UseResendInvitationParams): UseResendInvitationReturn {
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();

  const isProjectSubject = subjectType === "project";
  const isWorkspaceOrGroupOrTeamSubject =
    subjectType === "workspace" ||
    subjectType === "group" ||
    subjectType === "team";

  async function resendInvitation(member: SdbMemberTypes): Promise<void> {
    trackEvent({
      name: ProjectMemberEvents.resendInvitation,
      props: {
        dataType: isProjectSubject
          ? TableType.projectMembers
          : TableType.companyMembers,
      },
    });

    try {
      if (selectedProject?.id && isProjectSubject) {
        await faroCoreApiClient.projects.resendProjectMembershipInvite({
          companyId,
          projectId: selectedProject.id,
          email: member.email,
        });
      } else if (isWorkspaceOrGroupOrTeamSubject) {
        await faroCoreApiClient.company.resendCompanyMembershipInvite({
          companyId,
          userIdentity: member.email,
        });
      } else {
        // No valid context for resending invitation
        return;
      }

      openToast({
        title: "Invitation sent",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `resendInvite-${Date.now().toString()}`,
        title: "Could not send invitation",
        error,
      });
    }
  }

  function shouldShowResendInvitation(member: SdbMemberTypes): boolean {
    if (isMemberActive(member)) {
      return false;
    }
    if (isWorkspaceOrGroupOrTeamSubject && !canResendInvitationToCompany) {
      return false;
    }
    return true;
  }

  return { resendInvitation, shouldShowResendInvitation };
}
